import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Building2, Store, MapPin, Users, Star, Filter, ArrowUpRight, AlertTriangle } from 'lucide-react';
import { KPICard } from '../components/ui/KPICard';
import { Card } from '../components/ui/Card';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ZAxis, ComposedChart, Line, Bar, Cell } from 'recharts';
const levels = [{
  id: 'konzern',
  name: 'Konzern-Übersicht',
  icon: Building2
}, {
  id: 'region',
  name: 'Regionen',
  icon: MapPin
}, {
  id: 'markt',
  name: 'Märkte',
  icon: Store
}];
const marketsData = [{
  id: 'hamburg-hafencity',
  name: 'ALDI Nord Hamburg-Hafencity',
  region: 'Nord',
  rating: 4.3,
  staff: 25,
  size: 1200,
  staffPerSqm: 20.8
}, {
  id: 'berlin-kreuzberg',
  name: 'ALDI Nord Berlin-Kreuzberg',
  region: 'Ost',
  rating: 3.8,
  staff: 18,
  size: 1000,
  staffPerSqm: 18.0
}, {
  id: 'hannover-mitte',
  name: 'ALDI Nord Hannover-Mitte',
  region: 'Nord',
  rating: 4.6,
  staff: 22,
  size: 1100,
  staffPerSqm: 20.0
}, {
  id: 'koeln-ehrenfeld',
  name: 'ALDI Nord Köln-Ehrenfeld',
  region: 'West',
  rating: 3.5,
  staff: 14,
  size: 900,
  staffPerSqm: 15.5
}, {
  id: 'frankfurt-sachsenhausen',
  name: 'ALDI Nord Frankfurt-Sachsenhausen',
  region: 'Mitte',
  rating: 4.4,
  staff: 20,
  size: 1050,
  staffPerSqm: 19.0
}, {
  id: 'dortmund-innenstadt',
  name: 'ALDI Nord Dortmund-Innenstadt',
  region: 'West',
  rating: 3.9,
  staff: 16,
  size: 950,
  staffPerSqm: 16.8
}, {
  id: 'leipzig-sued',
  name: 'ALDI Nord Leipzig-Süd',
  region: 'Ost',
  rating: 4.5,
  staff: 24,
  size: 1150,
  staffPerSqm: 20.8
}, {
  id: 'muenster-zentrum',
  name: 'ALDI Nord Münster-Zentrum',
  region: 'West',
  rating: 4.2,
  staff: 19,
  size: 1000,
  staffPerSqm: 19.0
}, {
  id: 'osnabrueck-zentrum',
  name: 'ALDI Nord Osnabrück-Zentrum',
  region: 'Nord',
  rating: 4.1,
  staff: 18,
  size: 980,
  staffPerSqm: 18.3
}, {
  id: 'kiel-zentrum',
  name: 'ALDI Nord Kiel-Zentrum',
  region: 'Nord',
  rating: 4.4,
  staff: 21,
  size: 1050,
  staffPerSqm: 20.0
}];
const regionsData = [{
  id: 'nord',
  name: 'ALDI Nord Region Nord',
  rating: 4.2,
  staff: 88,
  size: 4500,
  staffPerSqm: 19.5,
  markets: 3
}, {
  id: 'ost',
  name: 'ALDI Nord Region Ost',
  rating: 3.9,
  staff: 75,
  size: 4200,
  staffPerSqm: 17.8,
  markets: 2
}, {
  id: 'west',
  name: 'ALDI Nord Region West',
  rating: 3.87,
  staff: 59,
  size: 3500,
  staffPerSqm: 16.8,
  markets: 3
}, {
  id: 'mitte',
  name: 'ALDI Nord Region Mitte',
  rating: 4.4,
  staff: 28,
  size: 1600,
  staffPerSqm: 17.5,
  markets: 1
}];
const konzernData = {
  id: 'konzern',
  name: 'ALDI Nord Gesamtkonzern',
  rating: 4.15,
  staff: 374,
  size: 20550,
  staffPerSqm: 18.2,
  markets: 15,
  regions: 4
};
const timeSeriesData = [{
  month: 'Feb 23',
  rating: 4.05,
  staffPerSqm: 17.1
}, {
  month: 'Mär 23',
  rating: 4.08,
  staffPerSqm: 17.2
}, {
  month: 'Apr 23',
  rating: 4.06,
  staffPerSqm: 17.3
}, {
  month: 'Mai 23',
  rating: 4.1,
  staffPerSqm: 17.5
}, {
  month: 'Jun 23',
  rating: 4.12,
  staffPerSqm: 17.7
}, {
  month: 'Jul 23',
  rating: 4.09,
  staffPerSqm: 17.6
}, {
  month: 'Aug 23',
  rating: 4.11,
  staffPerSqm: 17.8
}, {
  month: 'Sep 23',
  rating: 4.14,
  staffPerSqm: 17.9
}, {
  month: 'Okt 23',
  rating: 4.13,
  staffPerSqm: 18.0
}, {
  month: 'Nov 23',
  rating: 4.16,
  staffPerSqm: 18.1
}, {
  month: 'Dez 23',
  rating: 4.18,
  staffPerSqm: 18.2
}, {
  month: 'Jan 24',
  rating: 4.2,
  staffPerSqm: 18.2
}];
const calculateCorrelation = (data: Array<{
  rating: number;
  staffPerSqm: number;
}>) => {
  const n = data.length;
  const sumX = data.reduce((acc, d) => acc + d.staffPerSqm, 0);
  const sumY = data.reduce((acc, d) => acc + d.rating, 0);
  const sumXY = data.reduce((acc, d) => acc + d.staffPerSqm * d.rating, 0);
  const sumX2 = data.reduce((acc, d) => acc + d.staffPerSqm * d.staffPerSqm, 0);
  const sumY2 = data.reduce((acc, d) => acc + d.rating * d.rating, 0);
  const numerator = n * sumXY - sumX * sumY;
  const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
  return denominator === 0 ? 0 : numerator / denominator;
};
const CustomScatterTooltip = ({
  active,
  payload
}: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 text-sm" data-id="element-1038">
        <div className="font-semibold text-gray-900 mb-2" data-id="element-1039">{data.name}</div>
        <div className="space-y-1" data-id="element-1040">
          <div className="flex justify-between gap-4" data-id="element-1041">
            <span className="text-gray-500" data-id="element-1042">Rating:</span>
            <span className="font-medium" data-id="element-1043">{data.rating.toFixed(1)} ★</span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-1044">
            <span className="text-gray-500" data-id="element-1045">Personal/1000m²:</span>
            <span className="font-medium" data-id="element-1046">{data.staffPerSqm.toFixed(1)}</span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-1047">
            <span className="text-gray-500" data-id="element-1048">Mitarbeiter:</span>
            <span className="font-medium" data-id="element-1049">{data.staff}</span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-1050">
            <span className="text-gray-500" data-id="element-1051">Fläche:</span>
            <span className="font-medium" data-id="element-1052">
              {data.size.toLocaleString('de-DE')} m²
            </span>
          </div>
        </div>
      </div>;
  }
  return null;
};
export function PersonalReputationPage() {
  const [selectedLevel, setSelectedLevel] = useState<'konzern' | 'region' | 'markt'>('markt');
  const getScatterData = () => {
    switch (selectedLevel) {
      case 'markt':
        return marketsData.map(m => ({
          ...m,
          z: m.size
        }));
      case 'region':
        return regionsData.map(r => ({
          ...r,
          z: r.size
        }));
      case 'konzern':
        return [{
          ...konzernData,
          z: konzernData.size
        }];
    }
  };
  const scatterData = getScatterData();
  const correlation = calculateCorrelation(scatterData);
  const avgStaffPerSqm = scatterData.reduce((acc, d) => acc + d.staffPerSqm, 0) / scatterData.length;
  const avgRating = scatterData.reduce((acc, d) => acc + d.rating, 0) / scatterData.length;
  const understaffed = [...scatterData].filter(d => d.staffPerSqm < avgStaffPerSqm && d.rating < avgRating).sort((a, b) => a.staffPerSqm - b.staffPerSqm).slice(0, 3);
  const topPerformers = [...scatterData].filter(d => d.staffPerSqm >= avgStaffPerSqm).sort((a, b) => b.rating - a.rating).slice(0, 3);
  const potentialImprovement = understaffed.reduce((acc, d) => acc + (avgStaffPerSqm - d.staffPerSqm) * 0.08, 0) / understaffed.length || 0;
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-1053">
      <Card className="!p-4" data-id="element-1054">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-1055">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-1056">
            <Filter size={16} data-id="element-1057" />
            <span data-id="element-1058">Ebene:</span>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-1059">
            {levels.map(level => {
            const isSelected = selectedLevel === level.id;
            const Icon = level.icon;
            return <button key={level.id} onClick={() => setSelectedLevel(level.id as any)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isSelected ? 'bg-aldi-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1060">
                  <Icon size={14} data-id="element-1061" />
                  {level.name}
                </button>;
          })}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1062">
        <KPICard title="Korrelation Personal ↔ Rating" value={correlation.toFixed(2)} change={correlation > 0.6 ? 'Stark positiv' : correlation > 0.3 ? 'Moderat' : 'Schwach'} trend="up" icon={<TrendingUp size={18} data-id="element-1064" />} subtext="Pearson-Koeffizient" data-id="element-1063" />
        <KPICard title="Ø Personal pro 1000m²" value={avgStaffPerSqm.toFixed(1)} change="+1.2 vs. Vorjahr" trend="up" icon={<Users size={18} data-id="element-1066" />} subtext="Mitarbeiter" data-id="element-1065" />
        <KPICard title="Ø Bewertung" value={avgRating.toFixed(2)} change="+0.15" trend="up" icon={<Star size={18} data-id="element-1068" />} subtext="Google Rating" data-id="element-1067" />
        <KPICard title="Verbesserungspotenzial" value={`+${potentialImprovement.toFixed(2)}★`} change={`${understaffed.length} Märkte`} trend="up" icon={<AlertTriangle size={18} data-id="element-1070" />} subtext="bei Ø-Besetzung" data-id="element-1069" />
      </div>

      <Card data-id="element-1071">
        <div className="p-6" data-id="element-1072">
          <div className="flex items-center justify-between mb-6" data-id="element-1073">
            <div data-id="element-1074">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1075">
                Personalbesetzung vs. Kundenbewertung
              </h3>
              <p className="text-sm text-gray-500" data-id="element-1076">
                Blasengröße = Verkaufsfläche (m²) • Personal pro 1000m²
                Verkaufsfläche
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-1077">
              <div className="flex items-center gap-1" data-id="element-1078">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#639754'
              }} data-id="element-1079"></div>
                <span data-id="element-1080">Gut besetzt</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-1081">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#E03C32'
              }} data-id="element-1082"></div>
                <span data-id="element-1083">Unterbesetzt</span>
              </div>
            </div>
          </div>
          <div className="h-96" data-id="element-1084">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1085">
              <ScatterChart margin={{
              top: 20,
              right: 20,
              bottom: 20,
              left: 20
            }} data-id="element-1086">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1087" />
                <XAxis type="number" dataKey="staffPerSqm" name="Personal/1000m²" domain={[14, 22]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} label={{
                value: 'Personal pro 1000m² Verkaufsfläche',
                position: 'bottom',
                offset: 0,
                style: {
                  fontSize: 11,
                  fill: '#6b7280'
                }
              }} data-id="element-1088" />
                <YAxis type="number" dataKey="rating" name="Rating" domain={[3.2, 5]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} label={{
                value: 'Google Rating (Sterne)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#6b7280'
                }
              }} data-id="element-1089" />
                <ZAxis type="number" dataKey="z" range={[100, 800]} data-id="element-1090" />
                <Tooltip content={<CustomScatterTooltip data-id="element-1092" />} data-id="element-1091" />
                <Scatter name="Standorte" data={scatterData} fill="#00005f" data-id="element-1093">
                  {scatterData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.staffPerSqm >= avgStaffPerSqm ? '#639754' : entry.staffPerSqm >= avgStaffPerSqm - 1.5 ? '#FFD301' : '#E03C32'} fillOpacity={0.7} data-id="element-1094" />)}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm" data-id="element-1095">
            <div className="flex items-center gap-2" data-id="element-1096">
              <TrendingUp size={16} style={{
              color: '#639754'
            }} data-id="element-1097" />
              <span className="text-gray-600" data-id="element-1098">
                Korrelation:{' '}
                <span className="font-semibold" style={{
                color: correlation > 0.5 ? '#639754' : '#FFD301'
              }} data-id="element-1099">
                  {(correlation * 100).toFixed(0)}%
                </span>{' '}
                – Mehr Personal korreliert mit besseren Bewertungen
              </span>
            </div>
            <div className="text-gray-500" data-id="element-1100">
              Ø Besetzung: {avgStaffPerSqm.toFixed(1)} MA/1000m²
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1101">
        <Card data-id="element-1102">
          <div className="p-6" data-id="element-1103">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1104">
              <ArrowUpRight className="text-green-600" size={20} data-id="element-1105" /> Top
              Performer: Gut besetzt & hohe Bewertung
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1106">
              Märkte mit überdurchschnittlicher Besetzung und Rating
            </p>
            <div className="space-y-3" data-id="element-1107">
              {topPerformers.length > 0 ? topPerformers.map((item, index) => <motion.div key={item.id} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#e8f0e5'
            }} data-id="element-1108">
                    <div className="flex items-center gap-3" data-id="element-1109">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                  backgroundColor: '#639754'
                }} data-id="element-1110">
                        {index + 1}
                      </div>
                      <div data-id="element-1111">
                        <div className="font-medium text-gray-900 text-sm" data-id="element-1112">
                          {item.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1113">
                          {item.staff} MA • {item.size.toLocaleString('de-DE')}{' '}
                          m²
                        </div>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-1114">
                      <div className="font-bold text-lg" style={{
                  color: '#639754'
                }} data-id="element-1115">
                        {item.rating.toFixed(1)} ★
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1116">
                        {item.staffPerSqm.toFixed(1)} MA/1000m²
                      </div>
                    </div>
                  </motion.div>) : <div className="text-center py-8 text-gray-500" data-id="element-1117">
                  Keine Daten verfügbar
                </div>}
            </div>
          </div>
        </Card>

        <Card data-id="element-1118">
          <div className="p-6" data-id="element-1119">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1120">
              <AlertTriangle className="text-red-600" size={20} data-id="element-1121" />{' '}
              Handlungsbedarf: Unterbesetzte Märkte
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1122">
              Märkte mit unterdurchschnittlicher Besetzung und Rating
            </p>
            <div className="space-y-3" data-id="element-1123">
              {understaffed.length > 0 ? understaffed.map((item, index) => <motion.div key={item.id} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#fce8e7'
            }} data-id="element-1124">
                    <div className="flex items-center gap-3" data-id="element-1125">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                  backgroundColor: '#E03C32'
                }} data-id="element-1126">
                        !
                      </div>
                      <div data-id="element-1127">
                        <div className="font-medium text-gray-900 text-sm" data-id="element-1128">
                          {item.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1129">
                          {item.staff} MA • {item.size.toLocaleString('de-DE')}{' '}
                          m²
                        </div>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-1130">
                      <div className="font-bold text-lg" style={{
                  color: '#E03C32'
                }} data-id="element-1131">
                        {item.rating.toFixed(1)} ★
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1132">
                        {item.staffPerSqm.toFixed(1)} MA/1000m²
                      </div>
                    </div>
                  </motion.div>) : <div className="text-center py-8 text-gray-500" data-id="element-1133">
                  Keine unterbesetzten Märkte gefunden
                </div>}
            </div>
          </div>
        </Card>
      </div>

      <Card data-id="element-1134">
        <div className="p-6" data-id="element-1135">
          <div className="flex items-center justify-between mb-6" data-id="element-1136">
            <div data-id="element-1137">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1138">
                Entwicklung über Zeit
              </h3>
              <p className="text-sm text-gray-500" data-id="element-1139">
                Personalbesetzung und Kundenbewertung (12 Monate)
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm" data-id="element-1140">
              <div className="flex items-center gap-2" data-id="element-1141">
                <div className="w-3 h-3 rounded-full bg-aldi-blue" data-id="element-1142"></div>
                <span className="text-gray-600" data-id="element-1143">Personal/1000m²</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1144">
                <div className="w-6 h-0.5 bg-aldi-accent" data-id="element-1145"></div>
                <span className="text-gray-600" data-id="element-1146">Rating</span>
              </div>
            </div>
          </div>
          <div className="h-72" data-id="element-1147">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1148">
              <ComposedChart data={timeSeriesData} data-id="element-1149">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-1150" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} data-id="element-1151" />
                <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} domain={[16, 19]} label={{
                value: 'Personal/1000m²',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#9ca3af'
                }
              }} data-id="element-1152" />
                <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} domain={[3.9, 4.4]} label={{
                value: 'Rating',
                angle: 90,
                position: 'insideRight',
                style: {
                  fontSize: 11,
                  fill: '#9ca3af'
                }
              }} data-id="element-1153" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number, name: string) => [name === 'staffPerSqm' ? `${value.toFixed(1)} MA/1000m²` : value.toFixed(2), name === 'staffPerSqm' ? 'Personal' : 'Rating']} data-id="element-1154" />
                <Bar yAxisId="left" dataKey="staffPerSqm" fill="#00005f" radius={[4, 4, 0, 0]} barSize={30} data-id="element-1155" />
                <Line yAxisId="right" type="monotone" dataKey="rating" stroke="#009ee3" strokeWidth={3} dot={{
                fill: '#009ee3',
                strokeWidth: 2,
                r: 4
              }} data-id="element-1156" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>

      <Card data-id="element-1157">
        <div className="p-6" data-id="element-1158">
          <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1159">
            Detailübersicht{' '}
            {selectedLevel === 'markt' ? 'Märkte' : selectedLevel === 'region' ? 'Regionen' : 'Konzern'}
          </h3>
          <div className="overflow-x-auto" data-id="element-1160">
            <table className="w-full text-sm" data-id="element-1161">
              <thead data-id="element-1162">
                <tr className="border-b border-gray-200" data-id="element-1163">
                  <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-1164">
                    Name
                  </th>
                  {selectedLevel === 'markt' && <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-1165">
                      Region
                    </th>}
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1166">
                    Rating
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1167">
                    Personal
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1168">
                    Fläche
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1169">
                    MA/1000m²
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1170">
                    vs. Ø
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1171">
                {scatterData.map((item: any, index) => {
                const staffDiff = (item.staffPerSqm - avgStaffPerSqm) / avgStaffPerSqm * 100;
                return <tr key={item.id || index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1172">
                      <td className="py-3 px-4 font-medium text-gray-900" data-id="element-1173">
                        {item.name}
                      </td>
                      {selectedLevel === 'markt' && <td className="py-3 px-4 text-gray-500" data-id="element-1174">
                          {item.region}
                        </td>}
                      <td className="py-3 px-4 text-right" data-id="element-1175">
                        <span className={`font-medium ${item.rating >= 4.3 ? 'text-green-600' : item.rating >= 3.8 ? 'text-gray-900' : 'text-red-600'}`} data-id="element-1176">
                          {item.rating.toFixed(1)} ★
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900" data-id="element-1177">
                        {item.staff} MA
                      </td>
                      <td className="py-3 px-4 text-right text-gray-500" data-id="element-1178">
                        {item.size.toLocaleString('de-DE')} m²
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-1179">
                        <span className={`font-medium ${staffDiff >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1180">
                          {item.staffPerSqm.toFixed(1)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-1181">
                        <span className={`font-medium ${staffDiff >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-1182">
                          {staffDiff >= 0 ? '+' : ''}
                          {staffDiff.toFixed(1)}%
                        </span>
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>;
}